import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Label } from "flowbite-react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { Dialog } from "@components";
import type { AccountingInvoicePreview } from "../../api";
import { integrationCatalog } from "../../integrations/catalog";

interface TripletexInvoiceDialogProps {
  show: boolean;
  preview: AccountingInvoicePreview | null;
  allowCustomerCreate: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  onAllowCustomerCreateChange: (value: boolean) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const TripletexInvoiceDialog: FC<TripletexInvoiceDialogProps> = ({
  show,
  preview,
  allowCustomerCreate,
  isSubmitting,
  errorMessage,
  onAllowCustomerCreateChange,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation(["common", "invoice"]);
  const customerIsMissing = preview?.customer.resolution === "missing";
  const recoveryStage = preview?.recovery?.stage;
  const isRecovery = Boolean(recoveryStage);
  const canApproveMissingCustomer = Boolean(
    customerIsMissing &&
    allowCustomerCreate &&
    (!preview?.existingSubmission || recoveryStage === "customer"),
  );
  const tripletex = integrationCatalog.tripletex;

  return (
    <Dialog
      show={show}
      size="lg"
      title={t(
        isRecovery
          ? "invoice:accounting_integration.recovery_title"
          : "invoice:accounting_integration.create_title",
      )}
      toggleModal={onClose}
      isLoading={isSubmitting}
      addDisabled={
        !preview ||
        (customerIsMissing && !allowCustomerCreate) ||
        (!preview.canSubmit && !canApproveMissingCustomer)
      }
      buttonConfirmText={t(
        isRecovery
          ? "invoice:accounting_integration.recovery_action"
          : "invoice:accounting_integration.create_action",
      )}
      buttonCloseText={t("common:cancel")}
      confirmButton={onSubmit}
      content={
        preview ? (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <img
                src={tripletex.emblemSrc || tripletex.logoSrc}
                alt=""
                aria-hidden="true"
                className="h-10 w-10 shrink-0 rounded-lg object-contain"
              />
              <div>
                <p className="font-medium text-text">
                  {t(
                    isRecovery
                      ? "invoice:accounting_integration.recovery_summary"
                      : "invoice:accounting_integration.create_summary",
                  )}
                </p>
                <p className="mt-1 text-sm leading-6 text-secondary">
                  {t("invoice:accounting_integration.customer_not_sent")}
                </p>
              </div>
            </div>

            <dl className="divide-y divide-border border-y border-border">
              <div className="flex justify-between gap-4 py-3 text-sm">
                <dt className="text-secondary">
                  {t("invoice:accounting_integration.customer")}
                </dt>
                <dd className="text-right font-medium text-text">
                  {preview.customer.organizationNumber}
                </dd>
              </div>
              <div className="flex justify-between gap-4 py-3 text-sm">
                <dt className="text-secondary">
                  {t("invoice:accounting_integration.lines")}
                </dt>
                <dd className="text-right font-medium text-text">
                  {preview.lines.length}
                </dd>
              </div>
              <div className="flex justify-between gap-4 py-3 text-sm">
                <dt className="text-secondary">{t("invoice:pdf.sum_total")}</dt>
                <dd className="text-right font-medium text-text">
                  {preview.totals.total} {preview.currency}
                </dd>
              </div>
            </dl>

            {isRecovery ? (
              <div className="rounded-lg border border-brand-muted bg-brand-subtle p-4 text-sm leading-5 text-text dark:bg-brand-muted/20">
                {t("invoice:accounting_integration.recovery_explanation")}
              </div>
            ) : null}

            {customerIsMissing ? (
              <div className="rounded-lg border border-lgb-yellow-200 bg-warning-muted p-4">
                <p className="text-sm text-text">
                  {t("invoice:accounting_integration.customer_missing")}
                </p>
                <div className="mt-3 flex items-start gap-3">
                  <Checkbox
                    id="tripletex-create-customer"
                    checked={allowCustomerCreate}
                    onChange={(event) =>
                      onAllowCustomerCreateChange(event.target.checked)
                    }
                    className="mt-0.5 text-brand focus:ring-brand"
                  />
                  <Label
                    htmlFor="tripletex-create-customer"
                    className="cursor-pointer text-sm leading-5 text-text"
                  >
                    {t("invoice:accounting_integration.allow_customer_create")}
                  </Label>
                </div>
              </div>
            ) : null}

            {errorMessage ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
              >
                <HiOutlineExclamationCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            ) : null}
          </div>
        ) : null
      }
    />
  );
};
